import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Save, Globe, Palette, Mail, FileText, AlertTriangle, Truck, Zap, CheckCircle, XCircle, Loader2, Package, CreditCard, Copy, Banknote, Building, RefreshCw, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { applyPlatformBranding } from '@/lib/platformBranding';

export default function AdminGlobalSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [komshipTesting, setKomshipTesting] = useState(false);
  const [komshipTestResult, setKomshipTestResult] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/global-settings');
      setSettings(res.data);
      setForm({
        platform_name: res.data.platform_name || '',
        platform_logo_url: res.data.platform_logo_url || '',
        platform_favicon_url: res.data.platform_favicon_url || '',
        platform_domain: res.data.platform_domain || '',
        support_email: res.data.support_email || '',
        support_whatsapp: res.data.support_whatsapp || '',
        whatsapp_widget_enabled: res.data.whatsapp_widget_enabled || false,
        whatsapp_widget_message: res.data.whatsapp_widget_message || 'Halo, saya ingin bertanya tentang Tokomoe.',
        legal_tos_content: res.data.legal_tos_content || '',
        legal_privacy_content: res.data.legal_privacy_content || '',
        smtp_host: res.data.smtp_host || '',
        smtp_port: res.data.smtp_port || 587,
        smtp_user: res.data.smtp_user || '',
        smtp_password: res.data.smtp_password || '',
        smtp_from_name: res.data.smtp_from_name || '',
        smtp_from_email: res.data.smtp_from_email || '',
        email_mode: res.data.email_mode || 'smtp',
        mailketing_api_token: res.data.mailketing_api_token || '',
        mailketing_from_name: res.data.mailketing_from_name || 'Tokomoe',
        mailketing_from_email: res.data.mailketing_from_email || '',
        primary_color: res.data.primary_color || '#2D6CDF',
        secondary_color: res.data.secondary_color || '#6366f1',
        maintenance_mode: res.data.maintenance_mode || false,
        maintenance_message: res.data.maintenance_message || '',
        rajaongkir_api_key: res.data.rajaongkir_api_key || '',
        rajaongkir_account_type: res.data.rajaongkir_account_type || 'starter',
        rajaongkir_enabled: res.data.rajaongkir_enabled || false,
        komship_api_key: res.data.komship_api_key || '',
        komship_environment: res.data.komship_environment || 'sandbox',
        komship_enabled: res.data.komship_enabled || false,
        pakasir_enabled: res.data.pakasir_enabled || false,
        pakasir_slug: res.data.pakasir_slug || '',
        pakasir_api_key: res.data.pakasir_api_key || '',
        pakasir_mode: res.data.pakasir_mode || 'sandbox',
        tripay_enabled: res.data.tripay_enabled || false,
        tripay_merchant_code: res.data.tripay_merchant_code || '',
        tripay_api_key: res.data.tripay_api_key || '',
        tripay_private_key: res.data.tripay_private_key || '',
        tripay_mode: res.data.tripay_mode || 'sandbox',
        landing_cta_title: res.data.landing_cta_title || 'Siap Mulai Bisnis Online Anda?',
        landing_cta_subtitle: res.data.landing_cta_subtitle || 'Bergabung dengan 10,000+ pebisnis Indonesia yang sudah menggunakan Tokomoe. Daftar gratis, tanpa kartu kredit.',
        landing_cta_primary_btn: res.data.landing_cta_primary_btn || 'Daftar Gratis Sekarang',
        landing_cta_secondary_btn: res.data.landing_cta_secondary_btn || 'Lihat Harga',
        landing_cta_features: res.data.landing_cta_features || 'Gratis 14 hari, Setup 30 detik, Cancel kapan saja',
      });
      applyPlatformBranding({
        name: res.data.platform_name,
        faviconUrl: res.data.platform_favicon_url,
        logoUrl: res.data.platform_logo_url,
        primaryColor: res.data.primary_color,
      });
    } catch { toast.error('Gagal memuat pengaturan'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); }, []);

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const save = async (keys) => {
    setSaving(true);
    try {
      const payload = keys ? Object.fromEntries(keys.map(k => [k, form[k]])) : form;
      await api.put('/admin/global-settings', payload);
      toast.success('Pengaturan disimpan');
      if (!keys || keys.includes('platform_favicon_url') || keys.includes('platform_name') || keys.includes('primary_color')) {
        applyPlatformBranding({
          name: form.platform_name,
          faviconUrl: form.platform_favicon_url,
          logoUrl: form.platform_logo_url,
          primaryColor: form.primary_color,
        });
      }
    } catch { toast.error('Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const testRajaOngkir = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/rajaongkir-test');
      setTestResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Gagal menghubungi server';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const testKomship = async () => {
    setKomshipTesting(true);
    setKomshipTestResult(null);
    try {
      const res = await api.post('/admin/komship-test');
      setKomshipTestResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Gagal menghubungi server';
      setKomshipTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally { setKomshipTesting(false); }
  };

  const testEmail = async () => {
    setEmailTesting(true);
    setEmailTestResult(null);
    try {
      const res = await api.post('/admin/test-email');
      setEmailTestResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Gagal menghubungi server';
      setEmailTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally { setEmailTesting(false); }
  };

  const platformOrigin = (typeof window !== 'undefined' && window.location?.origin) || 'https://tokomoe.com';
  const pakasirCallbackUrl = `${platformOrigin}/api/webhooks/pakasir`;
  const tripayCallbackUrl = `${platformOrigin}/api/webhooks/tripay-gateway`;
  const billingReturnUrl = `${platformOrigin}/dashboard/billing`;

  const copyText = async (text, label = 'URL') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} disalin`);
    } catch {
      toast.error('Gagal menyalin');
    }
  };

  if (loading) return <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Global Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Konfigurasi platform secara keseluruhan</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="mb-6">
          <TabsTrigger value="general"><Globe size={15} className="mr-1.5" />Umum</TabsTrigger>
          <TabsTrigger value="branding"><Palette size={15} className="mr-1.5" />Branding</TabsTrigger>
          <TabsTrigger value="email"><Mail size={15} className="mr-1.5" />Email</TabsTrigger>
          <TabsTrigger value="legal"><FileText size={15} className="mr-1.5" />Legal</TabsTrigger>
          <TabsTrigger value="maintenance"><AlertTriangle size={15} className="mr-1.5" />Maintenance</TabsTrigger>
          <TabsTrigger value="shipping"><Truck size={15} className="mr-1.5" />Pengiriman</TabsTrigger>
          <TabsTrigger value="payment"><CreditCard size={15} className="mr-1.5" />Payment Gateway</TabsTrigger>
          <TabsTrigger value="moota"><Banknote size={15} className="mr-1.5" />Moota</TabsTrigger>
          <TabsTrigger value="landing-cta"><Zap size={15} className="mr-1.5" />Landing CTA</TabsTrigger>
        </TabsList>

        {/* GENERAL */}
        <TabsContent value="general">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">Informasi Platform</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Nama Platform</Label>
                  <Input value={form.platform_name} onChange={e => set('platform_name', e.target.value)} placeholder="Tokomoe" />
                </div>
                <div className="space-y-2">
                  <Label>Domain Platform</Label>
                  <Input value={form.platform_domain} onChange={e => set('platform_domain', e.target.value)} placeholder="tokomoe.com" />
                </div>
                <div className="space-y-2">
                  <Label>Email Support</Label>
                  <Input type="email" value={form.support_email} onChange={e => set('support_email', e.target.value)} placeholder="support@tokomoe.com" />
                </div>
                <div className="space-y-2">
                  <Label>WhatsApp Support</Label>
                  <Input value={form.support_whatsapp} onChange={e => set('support_whatsapp', e.target.value)} placeholder="628123456789" />
                </div>
                <div className="space-y-2">
                  <Label>URL Logo</Label>
                  <Input value={form.platform_logo_url} onChange={e => set('platform_logo_url', e.target.value)} placeholder="https://..." />
                </div>
                <div className="space-y-2">
                  <Label>URL Favicon</Label>
                  <Input value={form.platform_favicon_url} onChange={e => set('platform_favicon_url', e.target.value)} placeholder="https://..." />
                  {form.platform_favicon_url && (
                    <div className="flex items-center gap-2 pt-1">
                      <img src={form.platform_favicon_url} alt="Favicon preview" className="w-8 h-8 rounded border border-slate-200 object-contain bg-white" onError={(e) => { e.target.style.display = 'none'; }} />
                      <span className="text-xs text-slate-500">Preview favicon — simpan lalu refresh tab jika belum berubah</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base font-semibold">Floating WhatsApp Widget</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Tombol melayang di pojok kanan bawah halaman publik (Landing, Status, dll). Memakai nomor "WhatsApp Support" di atas.</p>
                  </div>
                  <Switch
                    checked={!!form.whatsapp_widget_enabled}
                    onCheckedChange={v => set('whatsapp_widget_enabled', v)}
                    data-testid="whatsapp-widget-toggle"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pesan Default (akan terisi otomatis di WhatsApp)</Label>
                  <Textarea
                    value={form.whatsapp_widget_message || ''}
                    onChange={e => set('whatsapp_widget_message', e.target.value)}
                    placeholder="Halo, saya ingin bertanya tentang Tokomoe."
                    rows={2}
                    disabled={!form.whatsapp_widget_enabled}
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={() => save(['platform_name','platform_domain','support_email','support_whatsapp','whatsapp_widget_enabled','whatsapp_widget_message','platform_logo_url','platform_favicon_url'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BRANDING */}
        <TabsContent value="branding">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base">Branding & Warna</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Warna Utama (Primary)</Label>
                  <div className="flex gap-3 items-center">
                    <input type="color" value={form.primary_color} onChange={e => set('primary_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                    <Input value={form.primary_color} onChange={e => set('primary_color', e.target.value)} className="flex-1 font-mono" placeholder="#2D6CDF" />
                  </div>
                  <div className="h-8 rounded-lg mt-2" style={{ background: form.primary_color }} />
                </div>
                <div className="space-y-2">
                  <Label>Warna Sekunder (Secondary)</Label>
                  <div className="flex gap-3 items-center">
                    <input type="color" value={form.secondary_color} onChange={e => set('secondary_color', e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                    <Input value={form.secondary_color} onChange={e => set('secondary_color', e.target.value)} className="flex-1 font-mono" placeholder="#6366f1" />
                  </div>
                  <div className="h-8 rounded-lg mt-2" style={{ background: form.secondary_color }} />
                </div>
              </div>
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-500 mb-3 font-medium">Preview Tombol</p>
                <div className="flex gap-3 flex-wrap">
                  <button className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: form.primary_color }}>Tombol Utama</button>
                  <button className="px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: form.secondary_color }}>Tombol Sekunder</button>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={() => save(['primary_color','secondary_color'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Branding'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMAIL */}
        <TabsContent value="email">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Konfigurasi Email</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Email platform untuk notifikasi admin & fallback tenant</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Mode Selector */}
              <div className="space-y-2">
                <Label>Mode Pengiriman Email</Label>
                <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={form.email_mode} onChange={e => set('email_mode', e.target.value)}>
                  <option value="mailketing">Mailketing API</option>
                  <option value="smtp">SMTP</option>
                </select>
              </div>

              {/* Mailketing Fields */}
              {form.email_mode === 'mailketing' && (
                <div className="space-y-5">
                  <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 flex gap-3">
                    <Mail size={18} className="text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-700 font-medium">Mailketing API</p>
                      <p className="text-xs text-blue-600 mt-1">Kirim email menggunakan Mailketing API. Dapatkan API token di <a href="https://mailketing.co.id" target="_blank" rel="noopener noreferrer" className="underline font-medium">mailketing.co.id</a></p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2 md:col-span-2">
                      <Label>API Token</Label>
                      <Input type="password" value={form.mailketing_api_token} onChange={e => set('mailketing_api_token', e.target.value)} placeholder="Masukkan API token Mailketing" />
                    </div>
                    <div className="space-y-2">
                      <Label>Nama Pengirim (From Name)</Label>
                      <Input value={form.mailketing_from_name} onChange={e => set('mailketing_from_name', e.target.value)} placeholder="Tokomoe" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Pengirim (From Email)</Label>
                      <Input type="email" value={form.mailketing_from_email} onChange={e => set('mailketing_from_email', e.target.value)} placeholder="noreply@tokomoe.com" />
                    </div>
                  </div>
                </div>
              )}

              {/* SMTP Fields */}
              {form.email_mode === 'smtp' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input value={form.smtp_host} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input type="number" value={form.smtp_port} onChange={e => set('smtp_port', parseInt(e.target.value))} placeholder="587" />
                  </div>
                  <div className="space-y-2">
                    <Label>Username / Email</Label>
                    <Input value={form.smtp_user} onChange={e => set('smtp_user', e.target.value)} placeholder="noreply@tokomoe.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>Password / App Password</Label>
                    <Input type="password" value={form.smtp_password} onChange={e => set('smtp_password', e.target.value)} placeholder="••••••••" />
                  </div>
                  <div className="space-y-2">
                    <Label>Nama Pengirim (From Name)</Label>
                    <Input value={form.smtp_from_name} onChange={e => set('smtp_from_name', e.target.value)} placeholder="Tokomoe" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Pengirim (From Email)</Label>
                    <Input type="email" value={form.smtp_from_email} onChange={e => set('smtp_from_email', e.target.value)} placeholder="noreply@tokomoe.com" />
                  </div>
                </div>
              )}

              {emailTestResult && (
                <div className={`p-4 rounded-xl border flex gap-3 ${emailTestResult.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                  {emailTestResult.success ? <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" />}
                  <p className={`text-sm ${emailTestResult.success ? 'text-emerald-700' : 'text-red-700'}`}>{emailTestResult.message}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={testEmail} disabled={emailTesting}>
                  {emailTesting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
                  {emailTesting ? 'Menguji...' : 'Test Kirim Email'}
                </Button>
                <Button onClick={() => save(['email_mode','mailketing_api_token','mailketing_from_name','mailketing_from_email','smtp_host','smtp_port','smtp_user','smtp_password','smtp_from_name','smtp_from_email'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Email'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LEGAL */}
        <TabsContent value="legal">
          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-base">Syarat & Ketentuan (ToS)</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={form.legal_tos_content}
                  onChange={e => set('legal_tos_content', e.target.value)}
                  placeholder="Tulis konten Syarat & Ketentuan platform di sini..."
                  rows={12}
                  className="font-mono text-sm"
                />
                <div className="flex justify-end">
                  <Button onClick={() => save(['legal_tos_content'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                    <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan ToS'}
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-base">Kebijakan Privasi</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={form.legal_privacy_content}
                  onChange={e => set('legal_privacy_content', e.target.value)}
                  placeholder="Tulis konten Kebijakan Privasi platform di sini..."
                  rows={12}
                  className="font-mono text-sm"
                />
                <div className="flex justify-end">
                  <Button onClick={() => save(['legal_privacy_content'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                    <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Privacy Policy'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* MAINTENANCE */}
        <TabsContent value="maintenance">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Mode Maintenance</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Aktifkan untuk menampilkan pesan maintenance ke semua pengguna</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900">Mode Maintenance</p>
                  <p className="text-sm text-slate-500">Semua halaman frontend akan menampilkan pesan maintenance</p>
                </div>
                <Switch
                  checked={form.maintenance_mode}
                  onCheckedChange={v => set('maintenance_mode', v)}
                />
              </div>
              {form.maintenance_mode && (
                <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 flex gap-3">
                  <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">Mode maintenance aktif. Semua user tidak bisa mengakses platform.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Pesan Maintenance</Label>
                <Textarea
                  value={form.maintenance_message}
                  onChange={e => set('maintenance_message', e.target.value)}
                  placeholder="Sistem sedang dalam maintenance. Akan kembali dalam 30 menit."
                  rows={4}
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={() => save(['maintenance_mode','maintenance_message'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SHIPPING / RAJAONGKIR */}
        <TabsContent value="shipping">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Integrasi RajaOngkir</CardTitle>
              <p className="text-sm text-slate-500 mt-1">API key RajaOngkir dikelola di sini dan digunakan oleh semua tenant untuk produk fisik</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900">Aktifkan RajaOngkir</p>
                  <p className="text-sm text-slate-500">Semua tenant bisa menggunakan kalkulasi ongkir otomatis untuk produk fisik</p>
                </div>
                <Switch
                  checked={form.rajaongkir_enabled}
                  onCheckedChange={v => set('rajaongkir_enabled', v)}
                />
              </div>
              {form.rajaongkir_enabled && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50 flex gap-3">
                  <Truck size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-emerald-700">RajaOngkir aktif. Tenant yang memilih provider "RajaOngkir" akan menggunakan API key ini.</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>API Key RajaOngkir</Label>
                  <Input type="password" value={form.rajaongkir_api_key} onChange={e => set('rajaongkir_api_key', e.target.value)} placeholder="Masukkan API key dari rajaongkir.com" />
                  <p className="text-xs text-slate-500">Dapatkan API key di <a href="https://rajaongkir.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">rajaongkir.com</a> → Collaborator Dashboard → Integration → API Key</p>
                </div>
                <div className="space-y-2">
                  <Label>Tipe Akun</Label>
                  <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={form.rajaongkir_account_type} onChange={e => set('rajaongkir_account_type', e.target.value)}>
                    <option value="starter">Starter</option>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                  </select>
                  <p className="text-xs text-slate-500">Tipe akun menentukan kurir & fitur yang tersedia</p>
                </div>
              </div>
              {testResult && (
                <div className={`p-4 rounded-xl border flex gap-3 ${testResult.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                  {testResult.success ? <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" />}
                  <p className={`text-sm ${testResult.success ? 'text-emerald-700' : 'text-red-700'}`}>{testResult.message}</p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={testRajaOngkir} disabled={testing || !form.rajaongkir_api_key}>
                  {testing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
                  {testing ? 'Menguji...' : 'Test Koneksi'}
                </Button>
                <Button onClick={() => save(['rajaongkir_api_key','rajaongkir_account_type','rajaongkir_enabled'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Pengaturan Ongkir'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Komship Delivery Orders */}
          <Card className="border-slate-200 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="text-base">Integrasi Komship (Delivery Order & COD)</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Komship digunakan untuk membuat pengiriman, COD, tracking AWB, dan pickup. Terpisah dari RajaOngkir.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900">Aktifkan Komship</p>
                  <p className="text-sm text-slate-500">Tenant bisa membuat delivery order, request pickup, dan tracking via Komship</p>
                </div>
                <Switch checked={form.komship_enabled} onCheckedChange={v => set('komship_enabled', v)} />
              </div>
              {form.komship_enabled && (
                <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 flex gap-3">
                  <Package size={18} className="text-blue-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700">Komship aktif. Tenant bisa membuat pengiriman dan mengelola COD melalui Komship API.</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>API Key Komship (Shipping Delivery)</Label>
                  <Input type="password" value={form.komship_api_key} onChange={e => set('komship_api_key', e.target.value)} placeholder="Masukkan API key Shipping Delivery" />
                  <p className="text-xs text-slate-500">Dapatkan di <a href="https://collaborator.komerce.id" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">collaborator.komerce.id</a> → Integration → API Key → Shipping Delivery</p>
                </div>
                <div className="space-y-2">
                  <Label>Environment</Label>
                  <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={form.komship_environment} onChange={e => set('komship_environment', e.target.value)}>
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                  <p className="text-xs text-slate-500">Gunakan Sandbox untuk testing. Pindah ke Production saat sudah siap.</p>
                </div>
              </div>
              {komshipTestResult && (
                <div className={`p-4 rounded-xl border flex gap-3 ${komshipTestResult.success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                  {komshipTestResult.success ? <CheckCircle size={18} className="text-emerald-600 shrink-0 mt-0.5" /> : <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" />}
                  <p className={`text-sm ${komshipTestResult.success ? 'text-emerald-700' : 'text-red-700'}`}>{komshipTestResult.message}</p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={testKomship} disabled={komshipTesting || !form.komship_api_key}>
                  {komshipTesting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Zap size={16} className="mr-2" />}
                  {komshipTesting ? 'Menguji...' : 'Test Koneksi'}
                </Button>
                <Button onClick={() => save(['komship_api_key','komship_environment','komship_enabled'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Pengaturan Komship'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAYMENT GATEWAY */}
        <TabsContent value="payment">
          <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 mb-6 flex gap-3">
            <CreditCard size={18} className="text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Payment Gateway untuk Subscription SaaS</p>
              <p>Konfigurasi gateway yang dipakai platform untuk menarik pembayaran berlangganan dari Tenant. Gateway tenant sendiri (di toko mereka) diatur terpisah pada dashboard tenant masing-masing.</p>
            </div>
          </div>

          {/* Pakasir */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Pakasir</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Payment gateway Pakasir untuk billing langganan SaaS.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900">Aktifkan Pakasir</p>
                  <p className="text-sm text-slate-500">Tagihan langganan Tenant dapat dibayar via Pakasir</p>
                </div>
                <Switch checked={!!form.pakasir_enabled} onCheckedChange={v => set('pakasir_enabled', v)} data-testid="pakasir-toggle" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Project Slug</Label>
                  <Input value={form.pakasir_slug} onChange={e => set('pakasir_slug', e.target.value)} placeholder="tokomoe-saas" disabled={!form.pakasir_enabled} />
                  <p className="text-xs text-slate-500">Slug project di dashboard Pakasir</p>
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input type="password" value={form.pakasir_api_key} onChange={e => set('pakasir_api_key', e.target.value)} placeholder="xxx123..." disabled={!form.pakasir_enabled} />
                  <p className="text-xs text-slate-500">Dapatkan di <a href="https://pakasir.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">pakasir.com</a> → Project → API Key</p>
                </div>
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm disabled:opacity-50" value={form.pakasir_mode} onChange={e => set('pakasir_mode', e.target.value)} disabled={!form.pakasir_enabled}>
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-1">URL untuk dikonfigurasi di dashboard Pakasir</p>
                  <p className="text-xs text-slate-500 mb-3">Salin URL berikut, lalu paste ke project Pakasir Anda di pakasir.com → Project Settings.</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Callback URL (Webhook)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={pakasirCallbackUrl} readOnly className="font-mono text-xs bg-slate-50" data-testid="pakasir-callback-url" />
                    <Button variant="outline" size="icon" onClick={() => copyText(pakasirCallbackUrl, 'Callback URL Pakasir')} aria-label="Copy callback URL"><Copy size={14} /></Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Pakasir akan POST status pembayaran ke URL ini.</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Redirect URL (Return setelah bayar)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={billingReturnUrl} readOnly className="font-mono text-xs bg-slate-50" data-testid="pakasir-return-url" />
                    <Button variant="outline" size="icon" onClick={() => copyText(billingReturnUrl, 'Redirect URL')} aria-label="Copy redirect URL"><Copy size={14} /></Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Browser tenant akan diarahkan ke URL ini setelah selesai bayar.</p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={() => save(['pakasir_enabled','pakasir_slug','pakasir_api_key','pakasir_mode'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Pengaturan Pakasir'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tripay */}
          <Card className="border-slate-200 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="text-base">Tripay</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Payment gateway Tripay sebagai alternatif untuk billing langganan SaaS.</p>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900">Aktifkan Tripay</p>
                  <p className="text-sm text-slate-500">Tagihan langganan Tenant dapat dibayar via Tripay (bank transfer, e-wallet, QRIS, kartu kredit, minimarket)</p>
                </div>
                <Switch checked={!!form.tripay_enabled} onCheckedChange={v => set('tripay_enabled', v)} data-testid="tripay-toggle" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label>Merchant Code</Label>
                  <Input value={form.tripay_merchant_code} onChange={e => set('tripay_merchant_code', e.target.value)} placeholder="T0001" disabled={!form.tripay_enabled} />
                  <p className="text-xs text-slate-500">Kode merchant dari Tripay Dashboard</p>
                </div>
                <div className="space-y-2">
                  <Label>Mode</Label>
                  <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm disabled:opacity-50" value={form.tripay_mode} onChange={e => set('tripay_mode', e.target.value)} disabled={!form.tripay_enabled}>
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input type="password" value={form.tripay_api_key} onChange={e => set('tripay_api_key', e.target.value)} placeholder="DEV-xxxxxxxxxxxx" disabled={!form.tripay_enabled} />
                  <p className="text-xs text-slate-500">API key untuk request transaksi</p>
                </div>
                <div className="space-y-2">
                  <Label>Private Key</Label>
                  <Input type="password" value={form.tripay_private_key} onChange={e => set('tripay_private_key', e.target.value)} placeholder="xxxxxxxxxxxxxxxx" disabled={!form.tripay_enabled} />
                  <p className="text-xs text-slate-500">Dipakai untuk verifikasi signature callback</p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900 mb-1">URL untuk dikonfigurasi di dashboard Tripay</p>
                  <p className="text-xs text-slate-500 mb-3">Salin URL berikut, lalu paste ke Tripay Merchant Dashboard → Pengaturan Merchant.</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Callback URL (Webhook)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={tripayCallbackUrl} readOnly className="font-mono text-xs bg-slate-50" data-testid="tripay-callback-url" />
                    <Button variant="outline" size="icon" onClick={() => copyText(tripayCallbackUrl, 'Callback URL Tripay')} aria-label="Copy callback URL"><Copy size={14} /></Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Tripay akan POST notifikasi status transaksi (HMAC-SHA256 signed) ke URL ini.</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Return URL (setelah bayar)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={billingReturnUrl} readOnly className="font-mono text-xs bg-slate-50" data-testid="tripay-return-url" />
                    <Button variant="outline" size="icon" onClick={() => copyText(billingReturnUrl, 'Return URL')} aria-label="Copy return URL"><Copy size={14} /></Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Tenant akan kembali ke halaman ini setelah pembayaran.</p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={() => save(['tripay_enabled','tripay_merchant_code','tripay_api_key','tripay_private_key','tripay_mode'])} disabled={saving} className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90">
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan Pengaturan Tripay'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MOOTA PLATFORM */}
        <TabsContent value="moota">
          <AdminMootaPlatform />
        </TabsContent>

        {/* LANDING CTA — bagian "Siap Mulai Bisnis Online Anda?" di landing page */}
        <TabsContent value="landing-cta">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Zap size={18} className="text-violet-600" /> Landing Page — Final CTA</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Edit teks di section terakhir landing page (yang biru-ungu sebelum footer). Perubahan langsung tampil setelah simpan.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label>Judul Utama</Label>
                  <Input value={form.landing_cta_title || ''} onChange={(e) => set('landing_cta_title', e.target.value)} placeholder="Siap Mulai Bisnis Online Anda?" />
                </div>
                <div>
                  <Label>Subjudul / Deskripsi</Label>
                  <Textarea
                    value={form.landing_cta_subtitle || ''}
                    onChange={(e) => set('landing_cta_subtitle', e.target.value)}
                    rows={3}
                    placeholder="Bergabung dengan 10,000+ pebisnis Indonesia..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Tombol Utama (Primary)</Label>
                    <Input value={form.landing_cta_primary_btn || ''} onChange={(e) => set('landing_cta_primary_btn', e.target.value)} placeholder="Daftar Gratis Sekarang" />
                  </div>
                  <div>
                    <Label>Tombol Sekunder (Ghost)</Label>
                    <Input value={form.landing_cta_secondary_btn || ''} onChange={(e) => set('landing_cta_secondary_btn', e.target.value)} placeholder="Lihat Harga" />
                  </div>
                </div>
                <div>
                  <Label>Badge Fitur (pisahkan dengan koma)</Label>
                  <Input
                    value={form.landing_cta_features || ''}
                    onChange={(e) => set('landing_cta_features', e.target.value)}
                    placeholder="Gratis 14 hari, Setup 30 detik, Cancel kapan saja"
                  />
                  <p className="text-xs text-slate-500 mt-1">Maks. 3-4 badge agar tampil rapi di mobile.</p>
                </div>

                {/* Live preview */}
                <div className="mt-6">
                  <Label className="text-xs text-slate-500">Preview</Label>
                  <div className="mt-2 rounded-xl p-6 text-center text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #6C5CE7 0%, #2D6CDF 100%)' }}>
                    <h3 className="text-2xl font-bold mb-2">{form.landing_cta_title || 'Siap Mulai Bisnis Online Anda?'}</h3>
                    <p className="text-sm text-blue-100 mb-4">{form.landing_cta_subtitle || '...'}</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                      <Button size="sm" className="bg-white text-[#2D6CDF] hover:bg-blue-50">{form.landing_cta_primary_btn || 'Daftar'}</Button>
                      <Button size="sm" variant="ghost" className="text-white hover:bg-white/10">{form.landing_cta_secondary_btn || 'Lihat Harga'}</Button>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-4 text-xs text-blue-200">
                      {(form.landing_cta_features || '').split(',').map((f, i) => f.trim() && (
                        <span key={i} className="flex items-center gap-1"><CheckCircle size={11} /> {f.trim()}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => save(['landing_cta_title','landing_cta_subtitle','landing_cta_primary_btn','landing_cta_secondary_btn','landing_cta_features'])}
                  disabled={saving}
                  className="bg-[#2D6CDF] hover:bg-[#2D6CDF]/90"
                >
                  <Save size={16} className="mr-2" />{saving ? 'Menyimpan...' : 'Simpan CTA'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AdminMootaPlatform() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [banks, setBanks] = useState([]);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState('');
  const [webhookInfo, setWebhookInfo] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/integrations/moota');
      setCfg(data);
      if (data.is_configured) {
        try {
          const r = await api.post('/admin/integrations/moota/banks');
          setBanks(r.data.banks || []);
        } catch { setBanks([]); }
        try {
          const w = await api.get('/admin/integrations/moota/webhook-info');
          setWebhookInfo(w.data);
        } catch { setWebhookInfo(null); }
      }
    } catch { toast.error('Gagal memuat'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refreshBanks = async () => {
    setBusy(true); setAction('refresh');
    try {
      const { data } = await api.post('/admin/integrations/moota/banks');
      setBanks(data.banks || []);
      toast.success(`${data.banks?.length || 0} rekening ditemukan`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    finally { setBusy(false); setAction(''); }
  };

  const saveToken = async () => {
    if (!token.trim()) return toast.error('Token wajib diisi');
    setBusy(true); setAction('token');
    try {
      const { data } = await api.put('/admin/integrations/moota/token', { api_token: token.trim() });
      toast.success(`Token platform tersimpan. ${data.banks?.length || 0} rekening.`);
      setBanks(data.banks || []);
      setToken('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Token tidak valid'); }
    finally { setBusy(false); setAction(''); }
  };

  const selectBank = async (b) => {
    setBusy(true); setAction(`bank-${b.bank_account_id}`);
    try {
      await api.put('/admin/integrations/moota/select-bank', {
        bank_account_id: b.bank_account_id,
        bank_label: `${b.label} - ${b.account_number} (${b.account_holder})`,
      });
      toast.success('Bank platform dipilih');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    finally { setBusy(false); setAction(''); }
  };

  const registerWebhook = async () => {
    setBusy(true); setAction('webhook');
    try {
      const { data } = await api.post('/admin/integrations/moota/register-webhook');
      toast.success(`Webhook platform: ${data.webhook_url}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal register webhook'); }
    finally { setBusy(false); setAction(''); }
  };

  const toggleActive = async () => {
    setBusy(true); setAction('toggle');
    try {
      await api.put('/admin/integrations/moota/toggle', { is_active: !cfg.is_active });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    finally { setBusy(false); setAction(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin" /></div>;

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Banknote size={20} className="text-emerald-600" /> Moota Platform (Billing SaaS)
          {cfg?.is_configured && (
            <Badge className={cfg.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
              {cfg.is_active ? 'Aktif' : 'Nonaktif'}
            </Badge>
          )}
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Token & rekening level platform untuk auto-verifikasi pembayaran langganan tenant. Berbeda dengan integrasi Moota di tenant masing-masing.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5"><Zap size={13} /> Moota API Token (Platform)</Label>
          {cfg?.is_configured ? (
            <Input value={cfg.api_token_masked} readOnly className="font-mono text-xs bg-slate-50" />
          ) : (
            <div className="flex items-center gap-2">
              <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste token dari app.moota.co - Platform Account" type="password" className="font-mono text-xs" />
              <Button onClick={saveToken} disabled={busy || !token.trim()} className="bg-emerald-600 hover:bg-emerald-700">
                {busy && action === 'token' ? <Loader2 size={14} className="animate-spin" /> : 'Hubungkan'}
              </Button>
            </div>
          )}
        </div>

        {cfg?.is_configured && (
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Building size={13} /> Rekening Platform</Label>
              <Button variant="outline" size="sm" onClick={refreshBanks} disabled={busy}>
                {busy && action === 'refresh' ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                Sync
              </Button>
            </div>
            {banks.length === 0 ? (
              <div className="p-4 rounded border border-dashed border-slate-200 bg-slate-50 text-center">
                <p className="text-sm text-slate-600">Belum ada rekening bank di akun Moota Anda.</p>
                <p className="text-xs text-slate-500 mt-1">
                  Tambah rekening di <a href="https://app.moota.co/bank" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">app.moota.co/bank</a> lalu klik Sync.
                  Atau lanjut register webhook untuk semua bank yang akan datang.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {banks.map((b) => {
                  const sel = cfg.bank_account_id === b.bank_account_id;
                  return (
                    <div key={b.bank_account_id} className={`flex items-center justify-between p-3 rounded-lg border ${sel ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{b.label} - {b.account_number}</p>
                        <p className="text-xs text-slate-500">a.n. {b.account_holder} - Saldo: Rp {Number(b.balance || 0).toLocaleString('id-ID')}</p>
                      </div>
                      {sel ? (
                        <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle size={11} /> Aktif</Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => selectBank(b)} disabled={busy}>
                          {busy && action === `bank-${b.bank_account_id}` ? <Loader2 size={11} className="animate-spin" /> : 'Pilih'}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {cfg?.is_configured && (
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <Label>Webhook Platform Auto-Match</Label>
            {!cfg.webhook_url ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">
                  Klik untuk generate URL + Secret Token webhook platform.
                </p>
                <Button onClick={registerWebhook} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
                  {busy && action === 'webhook' ? <Loader2 size={13} className="animate-spin mr-2" /> : <Zap size={13} className="mr-2" />}
                  Siapkan Webhook Platform
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs space-y-2">
                  <p className="font-semibold text-amber-900 flex items-center gap-1">
                    <AlertTriangle size={13} /> Pasang webhook di Moota dashboard
                  </p>
                  <ol className="list-decimal pl-5 space-y-1 text-amber-900">
                    <li>Buka <a href="https://app.moota.co/integrations/webhook" target="_blank" rel="noopener noreferrer" className="underline font-medium">app.moota.co/integrations/webhook</a></li>
                    <li>Klik <strong>Tambah Webhook</strong> → masukkan URL & Secret Token di bawah</li>
                    <li>Pilih <strong>Type: credit</strong> (atau both)</li>
                    <li>Simpan. Setelah itu mutasi akan otomatis di-push ke Tokomoe.</li>
                  </ol>
                </div>
                <div>
                  <Label className="text-xs">Webhook URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={cfg.webhook_url} readOnly className="font-mono text-[11px] bg-slate-50" />
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(cfg.webhook_url); toast.success('URL disalin'); }} title="Copy URL">
                      <Copy size={13} />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Secret Token</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={webhookInfo?.webhook_secret || '••• (load ulang halaman untuk lihat)'} readOnly className="font-mono text-[11px] bg-slate-50" />
                    <Button variant="outline" size="icon" onClick={() => { if (webhookInfo?.webhook_secret) { navigator.clipboard.writeText(webhookInfo.webhook_secret); toast.success('Secret disalin'); } }} title="Copy Secret">
                      <Copy size={13} />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Paste secret ini di field "Secret Token" saat add webhook di Moota dashboard.</p>
                </div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                  <CheckCircle size={11} /> URL + Secret siap. Tinggal paste di Moota.
                </Badge>
              </div>
            )}
          </div>
        )}

        {cfg?.is_configured && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <div>
              <Label>Status</Label>
              <p className="text-xs text-slate-500">Saat aktif, webhook Moota akan diproses untuk billing tenant.</p>
            </div>
            <Switch checked={!!cfg.is_active} onCheckedChange={toggleActive} disabled={busy} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
